/**
 * Deals list page (PRD §11.3) — the home screen for sales reps.
 *
 * Layout: a real table (sticky header, click-to-open rows) with the
 * columns reps actually scan: Contact, Account, Stage, Status, Value,
 * Assigned to, Next follow-up, Created. Default scope is "Mine" (active
 * deals assigned to the caller, sorted urgent → cold so the work-now
 * stuff floats to the top); managers/admins can flip to "All" to see
 * the team pipeline. A search box and stage / status / value / assignee
 * filter chips compose with the scope tab. Server pagination caps the
 * page at 25 rows.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Briefcase, Plus, Search, Trash2, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { type Column, DataTable } from "../components/DataTable";
import { InlinePicklist } from "../components/InlinePicklist";
import { SearchableUserPicker } from "../components/SearchableUserPicker";
import { DealStagePill, DealStatusPill, DealValueChip } from "../components/deals/Pills";
import {
  type Account,
  ApiError,
  type Contact,
  type Deal,
  type DealUpdate,
  accountsApi,
  contactsApi,
  dealsApi,
  isDealConflictDetail,
  usersApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { usePicklistOptions } from "../lib/picklists";

export const Route = createFileRoute("/_app/deals/")({
  component: DealsListPage,
});

const PAGE_SIZE = 25;
type Scope = "mine" | "all";

function DealsListPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isManagerPlus = user?.role === "admin" || user?.role === "manager";

  // Default scope: reps see their own work; managers see the team pipeline.
  const [scope, setScope] = useState<Scope>(isManagerPlus ? "all" : "mine");
  const [openOnly, setOpenOnly] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [stage, setStage] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [assignedTo, setAssignedTo] = useState<string>("");
  const [page, setPage] = useState(0);

  // Per-row in-flight + transient banner for inline-edit failures (e.g. the
  // §8.5 "close needs account" 409). The banner clears after a few seconds
  // OR when the user dismisses it.
  const [savingDealId, setSavingDealId] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!inlineError) return;
    const t = setTimeout(() => setInlineError(null), 6000);
    return () => clearTimeout(t);
  }, [inlineError]);

  const filters = {
    open_only: openOnly,
    mine_only: scope === "mine" || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    q: debouncedQ.trim() || undefined,
    stage: stage || undefined,
    status: status || undefined,
    value: value || undefined,
    assigned_to_id: assignedTo || undefined,
    sort: "urgency" as const,
  };

  const listQuery = useQuery({
    queryKey: ["deals", filters],
    queryFn: () => dealsApi.list(filters),
  });

  // Inline cell edit. We optimistically patch the cached list so the new
  // pill renders the moment the user picks; if the server rejects (409 /
  // 422 / 403) we roll back and surface a banner.
  const updateInline = useMutation({
    mutationFn: ({ id, body, share }: { id: string; body: DealUpdate; share?: boolean }) =>
      dealsApi.update(id, body, { share: share ?? false }),
    onMutate: async ({ id, body }) => {
      setSavingDealId(id);
      setInlineError(null);
      const prev = queryClient.getQueryData<typeof data>(["deals", filters]);
      if (prev) {
        queryClient.setQueryData(["deals", filters], {
          ...prev,
          items: prev.items.map((d) => (d.id === id ? { ...d, ...body } : d)),
        });
      }
      return { prev };
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["deals", filters], ctx.prev);
      // 409 assignee_lacks_account_access: reassigning to a rep who can't
      // see the parent account. Ask, then retry granting them access.
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        isDealConflictDetail(err.detail) &&
        err.detail.code === "assignee_lacks_account_access" &&
        !vars.share
      ) {
        if (window.confirm(err.detail.message)) {
          updateInline.mutate({ ...vars, share: true });
        }
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not save change.";
      setInlineError(msg);
    },
    onSettled: () => {
      setSavingDealId(null);
      // Re-sync from the server: stage transitions can flip closed_at,
      // status, etc., and the optimistic patch only updated the one field.
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
  });

  const usersQuery = useQuery({
    queryKey: ["users", "active"],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: isManagerPlus,
  });
  const usersById = useMemo(() => {
    const m = new Map<string, { id: string; email: string; name: string | null }>();
    for (const u of usersQuery.data?.items ?? []) m.set(u.id, u);
    return m;
  }, [usersQuery.data]);

  // Resolve contact + account names so the table isn't a wall of UUIDs.
  // One bulk query each is fine until volumes get large; we'll add a
  // by-ids endpoint when the dataset warrants it. Deals can carry
  // either or both parents (PRD §8.1 v1.2), so both lookup tables are
  // always relevant.
  const contactsQ = useQuery({
    queryKey: ["contacts", { limit: 200, offset: 0 }],
    queryFn: () => contactsApi.list({ limit: 200, offset: 0 }),
  });
  const contactsById = useMemo(() => {
    const m = new Map<string, Contact>();
    for (const c of contactsQ.data?.items ?? []) m.set(c.id, c);
    return m;
  }, [contactsQ.data]);

  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200, offset: 0 }],
    queryFn: () => accountsApi.list({ limit: 200, offset: 0 }),
  });
  const accountsById = useMemo(() => {
    const m = new Map<string, Account>();
    for (const a of accountsQ.data?.items ?? []) m.set(a.id, a);
    return m;
  }, [accountsQ.data]);

  /** Short label for the deal's primary parent — contact wins when
   *  set; otherwise the account name. Used by accessibility labels and
   *  the Stage/Status/Value picklist `aria-label`. */
  const dealPrimaryLabel = (deal: Deal): string => {
    if (deal.contact_id) {
      const c = contactsById.get(deal.contact_id);
      if (c) return c.name;
      return `Contact ${deal.contact_id.slice(0, 8)}…`;
    }
    if (deal.account_id) {
      const a = accountsById.get(deal.account_id);
      if (a) return a.name;
      return `Account ${deal.account_id.slice(0, 8)}…`;
    }
    return "Deal";
  };

  const data = listQuery.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const hasFilter = Boolean(debouncedQ || stage || status || value || assignedTo);

  function clearFilters() {
    setQ("");
    setDebouncedQ("");
    setStage("");
    setStatus("");
    setValue("");
    setAssignedTo("");
    setPage(0);
  }

  // Same role gate the detail page uses: basics (stage/status/value) need
  // ownership for Sales; assignee re-assignment needs Manager+. Keep these
  // in sync with the backend (PRD §10.5). When user is undefined (rare
  // pre-bootstrap render) fall back to read-only.
  function canEditBasics(deal: Deal): boolean {
    if (!user) return false;
    if (user.role === "admin" || user.role === "manager") return true;
    return deal.assigned_to_id === user.id || deal.created_by_id === user.id;
  }
  function canEditAdvanced(): boolean {
    if (!user) return false;
    return user.role === "admin" || user.role === "manager";
  }

  // Picklist-driven option lists. Active rows only — inline edits
  // shouldn't let a rep pick a deactivated value (the server would
  // 422 anyway). The pill components keep using the stored value
  // verbatim, so existing rows carrying a deactivated value still
  // render correctly.
  const stagePicklist = usePicklistOptions("deal_stage");
  const statusPicklist = usePicklistOptions("deal_status");
  const valuePicklist = usePicklistOptions("deal_value");
  const stageOptions = stagePicklist.data.map((opt) => ({
    value: opt.value,
    label: <DealStagePill stage={opt.value} />,
  }));
  const statusOptions = statusPicklist.data.map((opt) => ({
    value: opt.value,
    label: <DealStatusPill status={opt.value} />,
  }));
  const valueOptions = valuePicklist.data.map((opt) => ({
    value: opt.value,
    label: <DealValueChip value={opt.value} />,
  }));
  const stageFilterValues = stagePicklist.data.map((o) => o.value);
  const statusFilterValues = statusPicklist.data.map((o) => o.value);
  const valueFilterValues = valuePicklist.data.map((o) => o.value);

  const columns: Column<Deal>[] = [
    {
      header: "Parent",
      width: "min-w-[14rem]",
      cell: (deal) => {
        const contact = deal.contact_id ? contactsById.get(deal.contact_id) : null;
        const account = deal.account_id ? accountsById.get(deal.account_id) : null;
        // Display rule: contact wins for the primary line; account
        // shows as a secondary chip beside it. When only the account
        // is set, it becomes the primary line.
        const primary = contact
          ? contact.name
          : account
            ? account.name
            : deal.contact_id
              ? `Contact ${deal.contact_id.slice(0, 8)}…`
              : deal.account_id
                ? `Account ${deal.account_id.slice(0, 8)}…`
                : "—";
        const secondary = contact?.phone;
        const showAccountChip = Boolean(deal.contact_id && deal.account_id);
        return (
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <span className="truncate font-medium">{primary}</span>
              {showAccountChip && account && (
                <span
                  className="shrink-0 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                  title={`Account: ${account.name}`}
                >
                  @ {account.name}
                </span>
              )}
              {showAccountChip && !account && deal.account_id && (
                <span
                  className="shrink-0 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                  title="Account"
                >
                  @ {deal.account_id.slice(0, 8)}…
                </span>
              )}
            </div>
            {secondary && <div className="truncate font-mono text-xs opacity-60">{secondary}</div>}
          </div>
        );
      },
    },
    {
      header: "Stage",
      width: "w-32",
      cell: (deal) => (
        <InlinePicklist
          value={deal.stage}
          options={stageOptions}
          disabled={!canEditBasics(deal)}
          saving={savingDealId === deal.id}
          ariaLabel={`Change stage for ${dealPrimaryLabel(deal)}`}
          onChange={(next) => updateInline.mutate({ id: deal.id, body: { stage: next } })}
        >
          <DealStagePill stage={deal.stage} />
        </InlinePicklist>
      ),
    },
    {
      header: "Status",
      width: "w-28",
      cell: (deal) => (
        <InlinePicklist
          value={deal.status}
          options={statusOptions}
          disabled={!canEditBasics(deal)}
          saving={savingDealId === deal.id}
          ariaLabel={`Change status for ${dealPrimaryLabel(deal)}`}
          onChange={(next) => updateInline.mutate({ id: deal.id, body: { status: next } })}
        >
          <DealStatusPill status={deal.status} />
        </InlinePicklist>
      ),
    },
    {
      header: "Value",
      width: "w-20",
      cell: (deal) => (
        <InlinePicklist
          value={deal.value}
          options={valueOptions}
          disabled={!canEditBasics(deal)}
          saving={savingDealId === deal.id}
          ariaLabel={`Change value for ${dealPrimaryLabel(deal)}`}
          onChange={(next) => updateInline.mutate({ id: deal.id, body: { value: next } })}
        >
          <DealValueChip value={deal.value} />
        </InlinePicklist>
      ),
    },
    {
      header: "Assigned to",
      width: "w-36",
      hideOnSmall: true,
      cell: (deal) => {
        const assigneeOptions = [
          { value: "" as const, label: <span className="opacity-60">(unassigned)</span> },
          ...((usersQuery.data?.items ?? []).map((u) => ({
            value: u.id,
            label: (
              <span>
                {u.name || u.email} <span className="text-xs opacity-60">— {u.role}</span>
              </span>
            ),
          })) as { value: string; label: ReactNode }[]),
        ];
        const trigger = !deal.assigned_to_id ? (
          <span className="opacity-50">Unassigned</span>
        ) : (
          (() => {
            const u = usersById.get(deal.assigned_to_id);
            if (u) return <span>{u.name || u.email.split("@")[0]}</span>;
            if (deal.assigned_to_name) return <span>{deal.assigned_to_name}</span>;
            return <span className="text-xs opacity-50">Unknown user</span>;
          })()
        );
        return (
          <InlinePicklist
            value={deal.assigned_to_id ?? ""}
            options={assigneeOptions}
            disabled={!canEditAdvanced()}
            saving={savingDealId === deal.id}
            ariaLabel={`Reassign deal for ${dealPrimaryLabel(deal)}`}
            popoverClassName="min-w-[14rem]"
            onChange={(next) =>
              updateInline.mutate({
                id: deal.id,
                body: { assigned_to_id: next === "" ? null : next },
              })
            }
          >
            {trigger}
          </InlinePicklist>
        );
      },
    },
    {
      header: "Next follow-up",
      width: "w-36",
      hideOnSmall: true,
      cell: (deal) => <NextFollowUpCell at={deal.next_follow_up_at} />,
    },
    {
      header: "Created",
      width: "w-28",
      align: "right",
      hideOnSmall: true,
      cell: (deal) => (
        <span className="text-xs opacity-60">{new Date(deal.created_at).toLocaleDateString()}</span>
      ),
    },
  ];

  return (
    <div className="page-shell space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Deals</h1>
          <p className="text-sm opacity-60">
            {scope === "mine"
              ? "Your active opportunities, urgent first."
              : "Team pipeline, urgent first."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Link
              to="/deals/trash"
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5"
              aria-label="Trash"
            >
              <Trash2 className="size-4" aria-hidden />
              <span className="hidden sm:inline">Trash</span>
            </Link>
          )}
          <Link
            to="/deals/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="size-4" aria-hidden />
            <span className="hidden sm:inline">New deal</span>
            <span className="sm:hidden">New</span>
          </Link>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div
          role="tablist"
          aria-label="Deal scope"
          className="inline-flex rounded-md border border-[var(--border)] p-0.5 text-sm"
        >
          <ScopeTab
            active={scope === "mine"}
            onClick={() => {
              setScope("mine");
              setPage(0);
            }}
            label="Assigned to me"
          />
          <ScopeTab
            active={scope === "all"}
            onClick={() => {
              setScope("all");
              setPage(0);
            }}
            label="All"
          />
        </div>
        <div
          role="tablist"
          aria-label="Open / closed"
          className="inline-flex rounded-md border border-[var(--border)] p-0.5 text-sm"
        >
          <ScopeTab
            active={openOnly}
            onClick={() => {
              setOpenOnly(true);
              setPage(0);
            }}
            label="Active"
          />
          <ScopeTab
            active={!openOnly}
            onClick={() => {
              setOpenOnly(false);
              setPage(0);
            }}
            label="All states"
          />
        </div>
      </div>

      <section className="space-y-2 rounded-xl border border-[var(--border)] p-3">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 opacity-50"
            aria-hidden
          />
          <input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="Search by contact name, phone, email, requirements, notes…"
            className="w-full rounded-md border border-[var(--border)] bg-transparent py-2 pl-8 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setDebouncedQ("");
                setPage(0);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 opacity-50 hover:opacity-100"
              aria-label="Clear search"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <FilterSelect
            label="Stage"
            value={stage}
            onChange={(v) => {
              setStage(v);
              setPage(0);
            }}
            options={stageFilterValues}
          />
          <FilterSelect
            label="Status"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(0);
            }}
            options={statusFilterValues}
          />
          <FilterSelect
            label="Value"
            value={value}
            onChange={(v) => {
              setValue(v);
              setPage(0);
            }}
            options={valueFilterValues}
          />
          {isManagerPlus && (
            <SearchableUserPicker
              value={assignedTo}
              onChange={(v) => {
                setAssignedTo(v);
                setPage(0);
              }}
              users={usersQuery.data?.items ?? []}
              loading={usersQuery.isLoading}
              emptyLabel="All assignees"
              ariaLabel="Filter by assignee"
            />
          )}
          {hasFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-md px-2 py-1 text-xs underline opacity-70 hover:opacity-100 sm:ml-auto"
            >
              Clear filters
            </button>
          )}
        </div>
      </section>

      {inlineError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <span className="flex-1">{inlineError}</span>
          <button
            type="button"
            onClick={() => setInlineError(null)}
            className="rounded p-0.5 opacity-60 hover:opacity-100"
            aria-label="Dismiss"
          >
            <X className="size-4" />
          </button>
        </div>
      )}

      {listQuery.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : listQuery.error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Couldn't load deals: {(listQuery.error as Error).message}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-xs opacity-60">
            <span>
              {data?.total ?? 0} {data?.total === 1 ? "deal" : "deals"}
              {hasFilter ? " match your filters" : ""}
            </span>
          </div>
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(l) => l.id}
            rowHref={(l) => `/deals/${l.id}`}
            empty={<EmptyState scope={scope} hasFilter={hasFilter} />}
          />
          <Pager
            page={page}
            totalPages={totalPages}
            total={data?.total ?? 0}
            shown={data?.items.length ?? 0}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          />
        </>
      )}
    </div>
  );
}

function ScopeTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`rounded px-3 py-1 transition-colors ${
        active
          ? "bg-[var(--accent)] text-white"
          : "opacity-70 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
      }`}
    >
      {label}
    </button>
  );
}

// Cheap pluralizer for the filter placeholders ("All stages", "All
// statuses", "All values"). The set of words we feed it is closed and
// well-known, so a hand-rolled rule for the -s/-es endings is enough.
function pluralize(word: string): string {
  if (word.endsWith("s") || word.endsWith("x") || word.endsWith("z")) return `${word}es`;
  return `${word}s`;
}

function FilterSelect<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly T[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
      aria-label={label}
    >
      <option value="">All {pluralize(label.toLowerCase())}</option>
      {options.map((opt) => {
        const pretty = opt.replace(/_/g, " ");
        return (
          <option key={opt} value={opt}>
            {pretty.charAt(0).toUpperCase() + pretty.slice(1)}
          </option>
        );
      })}
    </select>
  );
}

function NextFollowUpCell({ at }: { at: string | null }) {
  if (!at) return <span className="text-xs opacity-50">—</span>;
  const date = new Date(at);
  const now = new Date();
  const overdueMs = now.getTime() - date.getTime();
  const isOverdue = overdueMs > 0;
  const isToday = date.toDateString() === now.toDateString();
  return (
    <span
      className={`text-xs ${isOverdue ? "font-medium text-red-600 dark:text-red-400" : isToday ? "font-medium text-amber-700 dark:text-amber-300" : "opacity-80"}`}
    >
      {date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}
      {isOverdue && " · overdue"}
    </span>
  );
}

function EmptyState({ scope, hasFilter }: { scope: Scope; hasFilter: boolean }) {
  if (hasFilter) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--border)] p-8 text-center">
        <Search className="mx-auto mb-3 size-7 opacity-40" aria-hidden />
        <p className="text-sm font-medium">No matches</p>
        <p className="mt-1 text-sm opacity-60">Try a different search or clear the filters.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <Briefcase className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium">
        {scope === "mine" ? "No active deals assigned to you" : "No active deals yet"}
      </p>
      <p className="mt-1 text-sm opacity-60">
        Create a deal from a contact to start tracking the deal.
      </p>
      <Link
        to="/deals/new"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
      >
        <Plus className="size-4" aria-hidden />
        Create your first deal
      </Link>
    </div>
  );
}

function Pager({
  page,
  totalPages,
  total,
  shown,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  total: number;
  shown: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between text-xs opacity-70">
      <span>
        Page {page + 1} of {totalPages} · {shown} shown
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={page === 0}
          className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
        >
          Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= totalPages - 1}
          className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
